import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Text, TextField, Modal, Banner, Checkbox } from '@shopify/polaris';

import BatchGrid from '../../partials/BatchGrid';
import LoadingSpinner from '../../partials/LoadingSpinner';
import { useGetBatchesQuery, useStartPickingMutation, useResumePickingMutation } from '../../../warehouse/store/batches';
import { getSessionEmail } from '../../../warehouse/utils/session';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { buttonProps, useDocumentTitle } from '../../../warehouse/utils/a11y';
import { UI_COLORS } from '../../../constants';

// Totes are filled one row at a time, top to bottom, so the first
// toteCount cells in row-major order are the ones in use
const getUsedTotes = (batch) =>
  Array.from({ length: batch.toteCount }, (_, i) => ({
    row: Math.floor(i / batch.totalColumns) + 1,
    column: (i % batch.totalColumns) + 1,
    color: UI_COLORS.cyan,
  }));

const PickingBatches = () => {
  useDocumentTitle('Picking');
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the picker permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('picker');

  // The parked switch swaps the list from the picking queue to batches
  // parked mid-pick - those can be claimed and resumed by any picker
  const [showParked, setShowParked] = useState(false);

  // Prepared batches can be claimed by this user; batches already in
  // 'picking' show up read-only so a picker sees what's being worked on
  const {
    data: prepared = [],
    isLoading: preparedLoading,
    error: preparedError,
    isError: isPreparedError,
    refetch: refetchPrepared,
  } = useGetBatchesQuery('prepared', { skip: showParked });
  const {
    data: picking = [],
    isLoading: pickingLoading,
    refetch: refetchPicking,
  } = useGetBatchesQuery('picking', { skip: showParked });
  const {
    data: parked = [],
    isLoading: parkedLoading,
    error: parkedError,
    isError: isParkedError,
    refetch: refetchParked,
  } = useGetBatchesQuery('picking_parked', { skip: !showParked });
  const [startPicking, { isLoading: isClaiming }] = useStartPickingMutation();
  const [resumePicking, { isLoading: isResuming }] = useResumePickingMutation();

  // A batch can briefly appear in both lists when a claim lands before the
  // prepared query refreshes - the picking entry is fresher, so it wins
  const batches = showParked
    ? parked
    : Array.from(
        new Map([...prepared, ...picking].map((batch) => [batch.id, batch])).values()
      );
  const isLoading = showParked ? parkedLoading : preparedLoading || pickingLoading;
  const listQueryError = showParked ? parkedError : preparedError;
  const isListQueryError = showParked ? isParkedError : isPreparedError;

  const refetchAll = useCallback(() => {
    if (showParked) {
      refetchParked();
    } else {
      refetchPrepared();
      refetchPicking();
    }
  }, [showParked, refetchParked, refetchPrepared, refetchPicking]);

  // Web routes remount when navigated to - refresh on mount/toggle
  useEffect(() => {
    refetchAll();
  }, [refetchAll]);

  // Batch pending cart-setup confirmation before picking starts
  const [confirmBatch, setConfirmBatch] = useState(null);
  const [cartInput, setCartInput] = useState('');
  const [cartError, setCartError] = useState('');
  // Parked batch pending resume confirmation
  const [resumeBatch, setResumeBatch] = useState(null);
  const [listError, setListError] = useState('');

  // The logged-in user's email, for spotting our own in-progress batches
  const [myEmail, setMyEmail] = useState('');
  useEffect(() => {
    getSessionEmail().then(email => setMyEmail(email || ''));
  }, []);

  const handleBatchPress = (batch) => {
    if (batch.status === 'picking_parked') {
      // Any picker can claim a parked batch and pick the work back up
      setResumeBatch(batch);
      return;
    }
    if (batch.status === 'picking') {
      // Already claimed - only the picker who claimed it can re-enter
      if (batch.picker !== myEmail || !myEmail) return;
      navigate(`/warehouse/app/picking/${batch.id}`);
      return;
    }
    setCartInput('');
    setCartError('');
    setConfirmBatch(batch);
  };

  const handleConfirmCartSetup = async () => {
    if (!confirmBatch || isClaiming) return;
    // A cart ID is required to start picking - it ties the batch to the
    // physical cart the totes are loaded into
    if (!cartInput.trim()) {
      setCartError('Please enter or scan a cart ID');
      return;
    }

    // Ask the backend to check the batch out to this picker. It only
    // succeeds while the batch is still 'prepared' and unclaimed.
    const result = await startPicking({ batchId: confirmBatch.id, cartId: cartInput.trim() });

    if ('error' in result) {
      const status = result.error?.status;
      setConfirmBatch(null);
      setCartInput('');
      setCartError('');
      setListError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 409
            ? `${confirmBatch.name} is already being picked by someone else.`
            : `Could not start picking ${confirmBatch.name}. Please try again.`
      );
      refetchAll();
      return;
    }

    setConfirmBatch(null);
    setCartInput('');
    setCartError('');
    setListError('');
    navigate(`/warehouse/app/picking/${confirmBatch.id}`);
  };

  // Claiming a parked batch checks it out to this picker and flips it
  // back to 'picking'; the saved picking_state is restored on the screen
  const handleConfirmResume = async () => {
    if (!resumeBatch || isResuming) return;

    const result = await resumePicking(resumeBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setResumeBatch(null);
      setListError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 409
            ? `${resumeBatch.name} is no longer parked - it may have been claimed or completed already.`
            : `Could not resume picking ${resumeBatch.name}. Please try again.`
      );
      refetchAll();
      return;
    }

    setResumeBatch(null);
    setListError('');
    navigate(`/warehouse/app/picking/${resumeBatch.id}`);
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  const renderBatchCard = (batch) => {
    const isPicking = batch.status === 'picking';
    const isParked = batch.status === 'picking_parked';
    const isMine = isPicking && myEmail !== '' && batch.picker === myEmail;
    const disabled = isPicking && !isMine;

    return (
      <div
        key={batch.id}
        className={`warehouse-batch-card${disabled ? ' warehouse-batch-card-disabled' : ''}`}
        {...buttonProps(() => handleBatchPress(batch), { disabled })}
      >
        <div className="warehouse-batch-header">
          <Text as="p" fontWeight="bold">{batch.name}</Text>
          {isParked && <span className="warehouse-badge warehouse-badge-warning">PARKED</span>}
          {isPicking && (
            <span className="warehouse-badge warehouse-badge-primary">
              {isMine ? 'PICKING - RESUME' : 'PICKING'}
            </span>
          )}
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Batch ID:</span>
          <span>{batch.id}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Items:</span>
          <span>{batch.itemCount}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Totes:</span>
          <span>{batch.toteCount}</span>
        </div>

        {batch.status === 'picking' && (
          <div className="warehouse-batch-info">
            <span className="warehouse-info-label">Picker:</span>
            <span>{batch.picker}</span>
          </div>
        )}

        {isParked && batch.pickingState?.picker_email ? (
          <div className="warehouse-batch-info">
            <span className="warehouse-info-label">Parked by:</span>
            <span>{batch.pickingState.picker_email}</span>
          </div>
        ) : null}

        <div className="warehouse-batch-preview">
          <span className="warehouse-info-label">Cart setup</span>
          <BatchGrid
            totalRows={batch.totalRows}
            totalColumns={batch.totalColumns}
            toteSize={24}
            gap={4}
            highlightedTotes={getUsedTotes(batch)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header">
        <div className="warehouse-header-row">
          <div>
            <h2>Picking</h2>
            <div className="warehouse-header-subtitle">
              {showParked
                ? `${batches.length} parked picking batches`
                : `${batches.length} batches in picking queue`}
            </div>
          </div>
          <div className="warehouse-header-actions">
            <div className="warehouse-switch-label">
              Parked
              <Checkbox checked={showParked} onChange={setShowParked} label="Show parked batches" labelHidden />
            </div>
            <Button onClick={refetchAll} disabled={isLoading} loading={isLoading}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {listError ? (
        <Banner tone="critical"><p>{listError}</p></Banner>
      ) : null}

      {isListQueryError ? (
        <Banner tone="critical">
          <p>
            {isAuthError(listQueryError)
              ? SESSION_EXPIRED_MESSAGE
              : showParked
                ? 'Could not load parked batches. Press refresh to try again.'
                : 'Could not load batches. Press refresh to try again.'}
          </p>
        </Banner>
      ) : null}

      <div className="warehouse-list">
        {isLoading && batches.length === 0 ? (
          <div className="warehouse-loading"><LoadingSpinner statusText="Loading batches..." /></div>
        ) : batches.length === 0 ? (
          <div className="warehouse-loading">
            <Text as="p">{showParked ? 'No parked picking batches.' : 'No batches in the picking queue.'}</Text>
          </div>
        ) : (
          batches.map(renderBatchCard)
        )}
      </div>

      <Modal
        open={confirmBatch !== null}
        onClose={() => setConfirmBatch(null)}
        title="Confirm Cart Setup"
      >
        {confirmBatch && (
          <Modal.Section>
            <Text as="p" fontWeight="bold">{confirmBatch.name} ({confirmBatch.id})</Text>
            <Text as="p">
              Set up your cart with {confirmBatch.toteCount} tote{confirmBatch.toteCount === 1 ? '' : 's'} in the highlighted positions before picking {confirmBatch.itemCount} items.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={confirmBatch.totalRows}
                totalColumns={confirmBatch.totalColumns}
                toteCount={confirmBatch.toteCount}
                toteSize={44}
                gap={8}
                highlightedTotes={getUsedTotes(confirmBatch)}
              />
            </div>
            <TextField
              label="Cart ID"
              value={cartInput}
              onChange={(value) => {
                setCartInput(value);
                setCartError('');
              }}
              placeholder="Enter or scan cart ID"
              autoComplete="off"
              error={cartError || undefined}
            />
            {isClaiming ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Checking out batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={() => setConfirmBatch(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmCartSetup}>Cart Ready</Button>
              </div>
            )}
          </Modal.Section>
        )}
      </Modal>

      <Modal
        open={resumeBatch !== null}
        onClose={() => setResumeBatch(null)}
        title="Resume Picking?"
      >
        {resumeBatch && (
          <Modal.Section>
            <Text as="p" fontWeight="bold">{resumeBatch.name} ({resumeBatch.id})</Text>
            <Text as="p">
              Check out this parked batch and continue picking cart{' '}
              <strong>{resumeBatch.cartId || 'unknown'}</strong> from where it was left off.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={resumeBatch.totalRows}
                totalColumns={resumeBatch.totalColumns}
                toteCount={resumeBatch.toteCount}
                toteSize={44}
                gap={8}
                highlightedTotes={getUsedTotes(resumeBatch)}
              />
            </div>
            {isResuming ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Checking out batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={() => setResumeBatch(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmResume}>Resume Picking</Button>
              </div>
            )}
          </Modal.Section>
        )}
      </Modal>
    </div>
  );
};

export default PickingBatches;
